import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Dropdown, Input, Modal } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import ConfigurableTable from "components/lib/ConfigurableTable"
import { CheckboxHeader } from "components/lib/UI"

type AssessmentReportModalProps = {
  isOpen: boolean
  generateAssessmentReport: (includeNotes: boolean, selectedFields: string[]) => void
  onClose: () => void
  challenge: OpenAPI.Schemas["Challenge"]
}

const templateFieldTypes = [
  {
    key: "shorttext",
    name: "Short text (one line)",
  },
  {
    key: "longtext",
    name: "Long text (paragraphs)",
  },
  {
    key: "numerictext",
    name: "Numerical text",
  },
  {
    key: "slider",
    name: "Slider",
  },
  {
    key: "dropdown",
    name: "Dropdown box",
  },
  {
    key: "optionMultiple",
    name: "Multiple selection",
  },
]

const BusinessProfileFields = [
  { sectionName: "Organisation profile", field: { id: "name", title: "Organisation Name" } },
  { sectionName: "Organisation profile", field: { id: "phoneNumber", title: "Phone number" } },
  { sectionName: "Organisation profile", field: { id: "websiteUrl", title: "Website URL" } },
  { sectionName: "Organisation profile", field: { id: "description", title: "Description" } },
  { sectionName: "Organisation profile", field: { id: "address.streetAddress", title: "Street address" } },
  { sectionName: "Organisation profile", field: { id: "address.country", title: "Country" } },
  { sectionName: "Organisation profile", field: { id: "address.region", title: "Region" } },
  { sectionName: "Organisation profile", field: { id: "address.city", title: "City" } },
  { sectionName: "Organisation profile", field: { id: "address.postcode", title: "Postcode" } },
  { sectionName: "Organisation profile", field: { id: "size", title: "Organisation size" } },
  { sectionName: "Organisation profile", field: { id: "type", title: "Organisation type" } },
  { sectionName: "Organisation profile", field: { id: "foundingYear", title: "Founding year" } },
  { sectionName: "Organisation profile", field: { id: "status", title: "Organisation status" } },
  { sectionName: "Organisation profile", field: { id: "creditsafeRegNo", title: "Organisation registration number" } },
  { sectionName: "Organisation profile", field: { id: "vatNo", title: "VAT registration number" } },
]

export const AssessmentReportModal = ({
  isOpen,
  generateAssessmentReport,
  onClose,
  challenge,
}: AssessmentReportModalProps) => {
  const [includeNotes, setIncludeNotes] = useState<boolean>(false)
  const [exportWithIdeaFields, setExportWithIdeaFields] = useState<boolean>(false)
  // const [flattenedFields, setFlattenedFields] = useState([])
  const [selectedFields, setSelectedFields] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [searchFieldType, setSearchFieldType] = useState("")
  const { t } = useTranslation()

  const toggleFieldSelection = (fieldId) => {
    setSelectedFields((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]))
  }

  const businessProfileSection = challenge?.ownerIdeaTemplate?.body?.find(
    (section) => section.type === "businessProfile",
  )

  const flattenedFields =
    challenge?.ownerIdeaTemplate?.body
      ?.filter((section) => section.type === "standard" || section.type === "edi" || !section.type)
      .flatMap((section) =>
        (section.fields || []).map((field) => {
          return {
            sectionName: section.name || "Untitled section",
            field: field,
            challenge,
          }
        }),
      )
      .concat(businessProfileSection ? BusinessProfileFields : []) || []

  const filteredFields = useMemo(() => {
    return flattenedFields.filter((item) => {
      const matchesSearch = item.sectionName.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType = searchFieldType ? item.field.type === searchFieldType : true

      return matchesSearch && matchesType
    })
  }, [flattenedFields, searchTerm, searchFieldType])
  return (
    <Modal open={isOpen}>
      <Modal.Header>Generate assessment report</Modal.Header>
      <Modal.Content scrolling>
        <p>Download an Excel report of all assessments from this {t("generic.challenge")}.</p>
        <CheckboxHeader
          checked={includeNotes}
          onChange={() => setIncludeNotes(!includeNotes)}
          header="Include assessor notes"
          as="h5"
          description="Include any notes the assessor left for each of their assessments."
        />
        <CheckboxHeader
          checked={exportWithIdeaFields}
          onChange={() => setExportWithIdeaFields(!exportWithIdeaFields)}
          as="h5"
          header={`Include ${t("generic.idea")} fields`}
          description={`Include fields from the ${t("generic.idea")} in the report.`}
        />
        {exportWithIdeaFields && (
          <>
            <h5>Selected fields</h5>
            <p>Select the fields you want to include in the report.</p>
            <div
              style={{
                display: "flex",
                gap: "1px",
                marginBottom: "10px",
              }}
            >
              <div style={{ marginBottom: "5px", flex: 1, minWidth: "200px" }}>
                <Input
                  icon="search"
                  placeholder="Search by section name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: "5px", minWidth: "200px" }}>
                <Dropdown
                  placeholder="Filter by field type"
                  clearable
                  selection
                  options={templateFieldTypes.map((type) => ({
                    key: type.key,
                    value: type.key,
                    text: type.name,
                  }))}
                  value={searchFieldType}
                  onChange={(_e, { value }) => setSearchFieldType(value as string)}
                />
              </div>
            </div>
            <ConfigurableTable
              containerStyle={{ maxHeight: 400, overflowY: "auto" }}
              tableKey="challengeSectionFieldImport"
              selectedKeys={selectedFields}
              keyExtractor={(item) => item.field.id}
              onSelect={(item) => {
                if (!item.disabled) {
                  toggleFieldSelection(item.field.id)
                }
              }}
              preventSelectAll
              columns={[
                {
                  key: "sectionName",
                  name: "Section",
                },
                {
                  key: "fieldTitle",
                  name: "Field",
                  render: ({ item }) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {item.disabled && (
                        <em style={{ color: "red", marginBottom: 4 }}>This field has already been added</em>
                      )}
                      <p style={{ margin: 0 }}>
                        {item.field.type === "row"
                          ? "Row divider"
                          : item.field.type === "infotext"
                            ? "Information text"
                            : item.field.title}
                      </p>
                    </div>
                  ),
                },
              ]}
              data={filteredFields}
            />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button primary onClick={() => generateAssessmentReport(includeNotes, selectedFields)}>
          Generate Report
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
